import { useSelector } from 'react-redux';
import { useCallback } from 'react';

import { FinanceOffer, FinanceResult } from 'state/financeOffers';

import useTranslate from 'hooks/useTranslate';
import { useSnackbar } from 'notistack';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import { selectCreditApplication } from 'state/creditApplication';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';
import unifiedPrepareCheckout, {
  UnifiedPrepareCheckoutError,
  UnifiedPrepareCheckoutRequest,
} from 'hooks/requests/unifiedPrepareCheckout';
import { interpretDenialStatusCodes } from 'hooks/requests/unifiedCheckout';

type PrepareCheckoutHook = (
  financeResult: FinanceResult,
  selectedOffer: FinanceOffer,
) => (request: {
  disclosureChecksum: string;
  braintreeNonce?: string;
  braintreeCardType?: 'DEBIT' | 'CREDIT';
}) => Promise<void>;

const usePrepareCheckout: PrepareCheckoutHook = (
  financeResult: FinanceResult,
  selectedOffer: FinanceOffer,
) => {
  const [unifiedPrepareCheckoutRequest] = unifiedPrepareCheckout();
  const t = useTranslate();

  const { enqueueSnackbar } = useSnackbar();
  const { push } = useNavigationHelpers();

  const {
    firstName,
    lastName,
    emailAddress,
    mobilePhone,
    order,
    shippingAddress,
    intendedPurchaseAmount,
  } = useSelector(selectCreditApplication);

  return useCallback(
    async ({ disclosureChecksum, braintreeCardType, braintreeNonce }) => {
      const type = selectedOffer.paymentProductType;
      const paymentAgreementId = selectedOffer.paymentAgreementId;
      const unifiedPrepareCheckoutValues: UnifiedPrepareCheckoutRequest = {
        firstName,
        lastName,
        emailAddress,
        mobilePhone,
        applicationId: financeResult.id,
        paymentAgreementId: paymentAgreementId,
        braintreeNonce,
        braintreeCardType,
        intendedPurchaseAmount,
        order,
        shippingAddress,
        paymentProductType: type,
        disclosureChecksum,
      };

      try {
        const prepareCheckoutResponse = await unifiedPrepareCheckoutRequest(
          unifiedPrepareCheckoutValues,
        );

        if (prepareCheckoutResponse.status === 'CHECKOUT_PREPARED') {
          push('/unified/financing-card-issuance');
        } else {
          switch (
            interpretDenialStatusCodes(prepareCheckoutResponse.statusCodes)
          ) {
            case 'fraud':
              push('/unified/checkout/place-order/fraud-alert');
              break;
            case 'fraudOfac':
              push('/unified/checkout/place-order/fraud-alert-ofac');
              break;
            case 'splitpayAuthFailureRetry':
              enqueueSnackbar(
                t('unified.placeOrder.error.genericSnackbarError'),
                {
                  variant: 'error',
                  persist: true,
                },
              );
              break;
            case 'pending':
              push('/unified/checkout/place-order/pending');
              break;
            default:
              // This is a technical glitch scenario
              enqueueSnackbar(
                t('unified.placeOrder.error.genericSnackbarError'),
                {
                  variant: 'error',
                  persist: true,
                },
              );
              break;
          }
        }
      } catch (e) {
        if (catchOnly(e, NonClientError, UnifiedPrepareCheckoutError)) {
          enqueueSnackbar(t('unified.placeOrder.error.genericSnackbarError'), {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [
      emailAddress,
      enqueueSnackbar,
      financeResult.id,
      firstName,
      intendedPurchaseAmount,
      lastName,
      mobilePhone,
      order,
      push,
      selectedOffer.paymentAgreementId,
      selectedOffer.paymentProductType,
      shippingAddress,
      t,
      unifiedPrepareCheckoutRequest,
    ],
  );
};

export default usePrepareCheckout;
